import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProductImage } from "@/components/ProductImage";
import { Lock, FlaskConical, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductQuickView } from "@/components/ProductQuickView";
import { Eye } from "lucide-react";

const purityOf = (product) => {
  const spec = (product.specifications || []).find((s) => /purity/i.test(s.key || ""));
  if (!spec) return null;
  const m = String(spec.value).match(/≥?\s*\d+(\.\d+)?\s*%/);
  return m ? m[0].replace(/\s+/g, " ") : null;
};

/** Large-bottle product card used on the home page (public: no price, no category). */
export const FeaturedProductCard = ({ product, eager = false }) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(false);
  const purity = purityOf(product);
  const form = (product.specifications || []).find((s) => /form/i.test(s.key || ""))?.value;
  return (
    <div className="h-full transition-transform duration-200 hover:-translate-y-0.5">
      <div
        data-testid="featured-product-card"
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-[0_1px_2px_rgba(8,21,37,0.06)] transition-shadow hover:shadow-[0_12px_32px_rgba(8,21,37,0.10)]"
      >
        <button
          type="button"
          onClick={() => setPreview(true)}
          data-testid="featured-product-preview-trigger"
          aria-label={`Quick preview of ${product.name}`}
          className="group relative block aspect-[4/5] w-full cursor-zoom-in overflow-hidden bg-white text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue/40"
        >
          {product.images && product.images[0] ? (
            <ProductImage
              src={product.images[0]}
              alt={product.name}
              loading={eager ? "eager" : "lazy"}
              fetchpriority={eager ? "high" : undefined}
              width={1600}
              height={1200}
              sizes={"(min-width: 1024px) 300px, (min-width: 640px) 45vw, 50vw"}
              className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.05]"
              data-testid="featured-product-image"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-300"><FlaskConical className="h-12 w-12" /></div>
          )}
          <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-navy/80 px-2.5 py-1 text-[11px] font-medium text-white opacity-0 backdrop-blur transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
            <Eye className="h-3 w-3" /> Quick preview
          </span>
        </button>
        <ProductQuickView product={product} open={preview} onOpenChange={setPreview} />
        <div className="flex flex-1 flex-col px-3 pb-3 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
          <h3 className="font-heading text-sm font-semibold leading-snug text-navy sm:text-base">
            <Link to={`/products/${product.id}`} className="hover:text-brand-blue">{product.name}</Link>
          </h3>
          <p className="mt-1 text-xs font-medium text-emerald-700">Research grade</p>
          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            {purity && <p><span className="font-semibold text-navy">{purity}</span> purity</p>}
            {form && <p className="hidden text-xs sm:block">{form}</p>}
          </div>
          <div className="mt-auto grid grid-cols-2 gap-2 pt-3 sm:pt-4">
            <Button variant="outline" size="sm" onClick={() => navigate(`/products/${product.id}`)} data-testid="featured-view-button"
              className="min-w-0 border-brand-blue/30 px-2 text-xs text-brand-blue hover:bg-brand-blue/5">
              Details <ArrowRight className="ml-1 hidden h-3.5 w-3.5 shrink-0 sm:inline" />
            </Button>
            <Button size="sm" onClick={() => navigate("/login")} data-testid="featured-pricing-button" className="min-w-0 bg-navy px-2 text-xs text-white hover:bg-navy/90">
              <Lock className="mr-1 hidden h-3.5 w-3.5 shrink-0 sm:inline" /><span className="hidden sm:inline lg:hidden">Member pricing</span><span className="sm:hidden lg:inline">Pricing</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
